import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Heart } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type {
  CreateVehicleSubscription,
  CreateVehicleSubscriptionResponse,
  VehicleSubscriptionsResponse,
} from '@//app/api/protected/subscriptions/vehicles';
import { useAuth } from '@//providers/AuthProvider';
import { useApiBaseUrl } from '@//providers/MarketSettingsProvider';
import { ToastTypeEnum, useToast } from '@//providers/ToastProvider';
import { httpClientPostAuthorizedJson } from '../../httpClient';
import { VEHICLE_SUBSCRIPTIONS_ENRICHED_CACHE_KEY } from './useEnrichedVehicleSubscriptions';
import { VEHICLE_SUBSCRIPTIONS_CACHE_KEY } from './useVehicleSubscriptions';

export interface CreateVehicleSubscriptionVariables {
  body: CreateVehicleSubscription;
}

const useCreateVehicleSubscription = () => {
  const t = useTranslations('other.toast.subscription.vehicle.create');
  const { decodedToken } = useAuth();
  const apiBaseUrl = useApiBaseUrl();
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const mutation = useMutation<
    CreateVehicleSubscriptionResponse,
    Error,
    CreateVehicleSubscriptionVariables
  >({
    mutationFn: (variables) => {
      const url = `${apiBaseUrl}/api/protected/subscriptions/vehicles`;
      return httpClientPostAuthorizedJson(url, variables.body);
    },
    onSuccess: (data) => {
      const next = queryClient.getQueryData<VehicleSubscriptionsResponse>([
        VEHICLE_SUBSCRIPTIONS_CACHE_KEY,
        decodedToken?.sub,
      ]);

      queryClient.setQueryData([VEHICLE_SUBSCRIPTIONS_CACHE_KEY, decodedToken?.sub], {
        successful: true,
        response: [...(next?.response || []), data.response],
      });

      queryClient.invalidateQueries({
        queryKey: [VEHICLE_SUBSCRIPTIONS_ENRICHED_CACHE_KEY, decodedToken?.sub],
      });

      addToast({
        type: ToastTypeEnum.Success,
        icon: <Heart aria-hidden="true" />,
        title: t('success.title'),
        description: t('success.description'),
      });
    },
    onError: () => {
      addToast({
        type: ToastTypeEnum.Error,
        title: t('error.title'),
        description: t('error.description'),
      });
    },
  });

  return mutation;
};

export default useCreateVehicleSubscription;
